import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Modal, Input, OptionalSection, Select } from '@/components/ui'
import { Checkbox } from '@/components/ui/Checkbox'
import { useCreateUnit, useCreatePropertyUnit } from '@/hooks/useUnits'
import { useCreateMeter, useCreateReading } from '@/hooks/useMeters'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { localToday } from '@/lib/date'
import type { Property } from '@/types'

// ── Props ─────────────────────────────────────────────────────────────────────

export interface AddUnitModalProps {
  /**
   * When roomTypeId is non-null, the modal is open (backward-compat control).
   * When `isOpen` is also provided, `isOpen` takes priority.
   */
  roomTypeId?: number | null
  /**
   * Explicit open override — use when you need to open the modal without a
   * pre-selected room type (e.g. property has zero room types).
   */
  isOpen?: boolean
  /**
   * Property context for the S2-3 heuristic:
   * - If room_types.length >= 1 → show room-type selector in an "Advanced"
   *   disclosure so the user can optionally group the unit into an existing type.
   * - If room_types.length === 0 → hide the selector and POST to the
   *   property-scoped endpoint so the server resolves the default room type
   *   without creating a duplicate.
   *
   * When omitted, the modal falls back to the previous behaviour (use
   * roomTypeId with useCreateUnit, no selector shown).
   */
  property?: Property
  /** Called when the modal should close (cancel OR after successful create). */
  onClose: () => void
  /**
   * Optional callback fired after the unit has been successfully committed to
   * the server. Meters continue in the background after this is called.
   */
  onCreated?: () => void
}

// ── Component ─────────────────────────────────────────────────────────────────

export function AddUnitModal({ roomTypeId, isOpen, property, onClose, onCreated }: AddUnitModalProps) {
  const { t: tu } = useTranslation('units')
  const { t: tc } = useTranslation('common')
  const { t: tm } = useTranslation('meters')
  const { t: tp } = useTranslation('properties')

  const createUnit = useCreateUnit()
  const createPropertyUnit = useCreatePropertyUnit()
  const createMeter = useCreateMeter()
  const createReading = useCreateReading()
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('units')

  const { data: accountSettings } = useAccountSettings()
  const isCondominium = accountSettings?.account_type === 'condominium'
  const { timezone } = useLocalizationConfig()

  // ── S2-3 heuristic: room-type context ────────────────────────────────────
  // Use the property prop when present; otherwise fall back to legacy roomTypeId behaviour.
  const propertyRoomTypes = property?.room_types ?? null
  const hasRoomTypes = propertyRoomTypes !== null && propertyRoomTypes.length >= 1
  // Show room-type selector only when property has ≥1 existing room type.
  const showRoomTypeSelector = propertyRoomTypes !== null && hasRoomTypes

  // The effective room type to create under when using useCreateUnit.
  // Default: roomTypeId from prop, else first existing room type.
  const defaultRoomTypeId = roomTypeId ?? (propertyRoomTypes && propertyRoomTypes.length > 0
    ? propertyRoomTypes[0].id
    : null)
  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState<number | null>(defaultRoomTypeId)

  // Sync selectedRoomTypeId when roomTypeId changes (modal reopens for a
  // different room type) — mirrors the existing isCondominium sync pattern.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- prop-driven sync when modal reopens
    setSelectedRoomTypeId(
      roomTypeId != null
        ? roomTypeId
        : propertyRoomTypes && propertyRoomTypes.length > 0
          ? propertyRoomTypes[0].id
          : null
    )
  }, [roomTypeId]) // eslint-disable-line react-hooks/exhaustive-deps -- propertyRoomTypes stable from RQ

  // ── Modal open state ──────────────────────────────────────────────────────
  // isOpen prop takes priority; fall back to `roomTypeId !== null` for backward compat.
  const effectivelyOpen = isOpen !== undefined ? isOpen : roomTypeId != null

  // ── Unit form state ───────────────────────────────────────────────────────
  const [unitName, setUnitName] = useState('')
  const [unitPrice, setUnitPrice] = useState('')
  const [unitAddMeters, setUnitAddMeters] = useState(!isCondominium)
  const [waterReading, setWaterReading] = useState('')
  const [electricityReading, setElectricityReading] = useState('')
  const [unitAreaSqm, setUnitAreaSqm] = useState('')
  const [unitShareRatio, setUnitShareRatio] = useState('')
  const [unitSubmitting, setUnitSubmitting] = useState(false)

  // Reset the meters checkbox default if the account type changes mid-session
  // (e.g. owner toggles apartment → condominium in another tab). The setState
  // here is intentional — it syncs an externally-driven default into local
  // state when the source changes.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- cross-tab default sync
    setUnitAddMeters(!isCondominium)
  }, [isCondominium])

  function handleClose() {
    setUnitName('')
    setUnitPrice('')
    setUnitAddMeters(!isCondominium)
    setWaterReading('')
    setElectricityReading('')
    setUnitAreaSqm('')
    setUnitShareRatio('')
    setUnitSubmitting(false)
    // Reset room-type selector to the default for this property
    setSelectedRoomTypeId(defaultRoomTypeId)
    onClose()
  }

  async function handleCreateUnit() {
    if (!unitName.trim() || !unitPrice.trim()) return
    setUnitSubmitting(true)
    const trimmedName = unitName.trim()
    const addMeters = unitAddMeters
    const water = waterReading
    const electricity = electricityReading

    let unitId: number

    try {
      if (propertyRoomTypes !== null && !hasRoomTypes && property) {
        // S2-3: property has zero room types → use property-scoped endpoint.
        // The server resolves (or creates) the default room type without
        // duplicating one that may already exist under a non-default name.
        const result = await createPropertyUnit.mutateAsync({
          propertyId: property.id,
          name: trimmedName,
          price: Number(unitPrice),
          ...(unitAreaSqm.trim() !== '' && { area_sqm: Number(unitAreaSqm) }),
          ...(unitShareRatio.trim() !== '' && { share_ratio: Number(unitShareRatio) }),
        })
        unitId = result.id
      } else {
        // Legacy / ≥1 room types path: use room-type-scoped endpoint.
        const effectiveRoomTypeId = selectedRoomTypeId ?? roomTypeId
        if (!effectiveRoomTypeId) {
          // Guard: no room type available (shouldn't happen but be defensive)
          setUnitSubmitting(false)
          return
        }
        const result = await createUnit.mutateAsync({
          roomTypeId: effectiveRoomTypeId,
          name: trimmedName,
          price: Number(unitPrice),
          ...(unitAreaSqm.trim() !== '' && { area_sqm: Number(unitAreaSqm) }),
          ...(unitShareRatio.trim() !== '' && { share_ratio: Number(unitShareRatio) }),
        })
        unitId = result.data.id
      }
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
      setUnitSubmitting(false)
      return
    }

    // Unit is committed — close the modal + show success immediately so the
    // operator isn't blocked by a slow meter POST (today's prod incident:
    // puma workers wedged on the meter request, modal spun forever, no
    // recovery path). Meter creation continues in the background and any
    // failure surfaces as its own toast.
    addToast({ type: 'success', message: tu('messages.created') })
    onCreated?.()
    handleClose()

    if (!addMeters) return

    const meterSpecs: Array<{ type: 'water' | 'electricity'; reading: string }> = [
      { type: 'water', reading: water },
      { type: 'electricity', reading: electricity },
    ]
    for (const spec of meterSpecs) {
      try {
        const meterResult = await createMeter.mutateAsync({
          unitId,
          meter_type: spec.type,
          serial_number: `${spec.type === 'water' ? 'W' : 'E'}-${trimmedName}`,
        })
        const meter = meterResult.data
        if (spec.reading.trim() !== '') {
          const value = Number(spec.reading)
          // EP-55 F-002 (Part B): opening reading saved with read_at = ONE DAY
          // BEFORE today (YYYY-MM-DD format, matches existing data-shape
          // convention). Eliminates the same-day timestamp tie that caused
          // Michael's qty=0 utility rows when Step 6 readings landed at the
          // same `read_at` as these openings. The backend SQL tie-breaker
          // (Part A in PricingCalculator#latest_reading_for) is the durable
          // defence-in-depth. Consumer audit verified safe — see commit.
          // DAT-1: "today" is the account's local calendar day (localToday),
          // not `Date.now() - 24h` read back as a UTC slice: that read the
          // UTC day, which during the account's early morning hours (e.g.
          // Asia/Bangkok 00:00-07:00) is still yesterday, landing read_at two
          // days back instead of one.
          const [ty, tm, td] = localToday(timezone).split('-').map(Number)
          const yesterday = new Date(ty, tm - 1, td)
          yesterday.setDate(yesterday.getDate() - 1)
          const yyyy = yesterday.getFullYear()
          const mm = String(yesterday.getMonth() + 1).padStart(2, '0')
          const dd = String(yesterday.getDate()).padStart(2, '0')
          await createReading.mutateAsync({
            meterId: meter.id,
            previous_value: value,
            current_value: value,
            read_at: `${yyyy}-${mm}-${dd}`,
          })
        }
      } catch (err) {
        addToast({
          type: 'error',
          message: `${tm('types.' + spec.type)}: ${translateError(err)}`,
        })
      }
    }
  }

  return (
    <Modal
      isOpen={effectivelyOpen}
      onClose={handleClose}
      title={tu('actions.add_unit')}
      footer={
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={handleClose} disabled={unitSubmitting}>
            {tc('actions.cancel')}
          </Button>
          <Button
            data-testid="add-unit-submit"
            onClick={handleCreateUnit}
            loading={unitSubmitting}
            disabled={!unitName.trim() || !unitPrice.trim()}
          >
            {tc('actions.create')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Input
          name="unit_name"
          label={tu('labels.unit_name')}
          placeholder={tu('placeholders.unit_name')}
          value={unitName}
          onChange={(e) => setUnitName(e.target.value)}
          autoFocus
        />
        <Input
          name="unit_price"
          label={tu('labels.monthly_rent_thb')}
          type="number"
          placeholder={tu('placeholders.price_example')}
          value={unitPrice}
          onChange={(e) => setUnitPrice(e.target.value)}
        />
        {isCondominium && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              name="unit_area_sqm"
              label={tu('labels.area_sqm')}
              type="number"
              step="0.01"
              value={unitAreaSqm}
              onChange={(e) => setUnitAreaSqm(e.target.value)}
              data-testid="unit-area-sqm"
            />
            <Input
              name="unit_share_ratio"
              label={tu('labels.share_ratio')}
              type="number"
              step="0.0001"
              value={unitShareRatio}
              onChange={(e) => setUnitShareRatio(e.target.value)}
              data-testid="unit-share-ratio"
            />
          </div>
        )}
        {!isCondominium && (
          <>
            <Checkbox
              size="sm"
              checked={unitAddMeters}
              onChange={(e) => setUnitAddMeters(e.target.checked)}
              label={tm('section.toggle_label')}
            />
            {unitAddMeters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-6">
                <Input
                  label={tm('section.water')}
                  type="number"
                  placeholder={tm('section.opening_reading_placeholder')}
                  value={waterReading}
                  onChange={(e) => setWaterReading(e.target.value)}
                />
                <Input
                  label={tm('section.electricity')}
                  type="number"
                  placeholder={tm('section.opening_reading_placeholder')}
                  value={electricityReading}
                  onChange={(e) => setElectricityReading(e.target.value)}
                />
              </div>
            )}
          </>
        )}

        {/* ── S2-3: Advanced room-type grouping disclosure ─────────────────
            Shown only when the property already has ≥1 room type AND the
            modal is open. Gate on effectivelyOpen so the select options are
            not rendered into a closed <dialog>, which would cause "multiple
            elements with text X" in tests that also render the same room-type
            names in the parent list. */}
        {effectivelyOpen && showRoomTypeSelector && property && (
          <OptionalSection
            id={`add-unit-room-type-${property.id}`}
            label={tp('rooms.group_into_room_types')}
            defaultOpen={false}
          >
            <div className="space-y-2">
              <Select
                id={`add-unit-room-type-select-${property.id}`}
                label={tu('labels.room_type')}
                value={String(selectedRoomTypeId ?? '')}
                onChange={(e) => setSelectedRoomTypeId(Number(e.target.value))}
                options={(propertyRoomTypes ?? []).map((rt) => ({
                  value: String(rt.id),
                  label: rt.name,
                }))}
                data-testid="add-unit-room-type-select"
              />
            </div>
          </OptionalSection>
        )}
      </div>
    </Modal>
  )
}
